import { CategoryBySlugDocument } from "./../graphql/Category.mjs";
import { useSetLocaleToCacheParams } from "./i18n.mjs";
import { useCachedSsrRef } from "./serverCache.mjs";
import { CategoryListDocument } from "../graphql/Category.mjs";
import { toNonNullable } from "../utils/collections.mjs";
import { useQuery } from "villus";
import { computed } from "vue";
function fetchCategories() {
  const { execute, error, isFetching } = useQuery({
    query: CategoryListDocument.toString(),
    fetchOnMount: false
  });
  return {
    execute,
    error,
    isFetching
  };
}
export async function useCategories(_fetchCategories = fetchCategories) {
  const { cacheParam } = useSetLocaleToCacheParams();
  const cachedCategories = useCachedSsrRef(cacheParam("categories"), []);
  const { execute } = _fetchCategories();
  const categories = await (async () => {
    if (cachedCategories.value.length) {
      return cachedCategories.value;
    }
    const { error, data } = await execute();
    if (error) {
      throw new Error(error.message);
    }
    cachedCategories.value = sortByPosition(
      toNonNullable(data?.categories?.items)?.filter((c) => !!c)?.map(mapCategoryTree)
    );
    return cachedCategories.value;
  })();
  const flatCategories = computed(() => {
    return flatten(categories || []);
  });
  const findBySlug = (slug) => {
    return flatCategories.value.find(
      (c) => "url_key" in c && c.url_key === slug
    );
  };
  const findByName = (name) => {
    return flatCategories.value.find(
      (c) => "name" in c && c.name.toLowerCase() === name.toLowerCase()
    );
  };
  return {
    categories: computed(() => categories),
    findBySlug,
    flatCategories: flatten(categories || []),
    findByName
  };
}
export async function useFetchCategoryBySlug(slug) {
  const { execute } = useQuery({
    query: CategoryBySlugDocument.toString(),
    variables: { slug },
    fetchOnMount: false
  });
  const { data, error } = await execute();
  if (error || !data?.categories?.[0]) {
    return null;
  }
  return mapCategoryTree(
    data?.categories?.filter((c) => !!c)?.[0]
  );
}
export async function useMegaMenuCategories(_fetchCategories = fetchCategories) {
  const { cacheParam } = useSetLocaleToCacheParams();
  const cachedCategories = useCachedSsrRef(cacheParam("megaMenuCategories"), []);
  const { execute } = _fetchCategories();
  const categories = await (async () => {
    if (cachedCategories.value?.length) {
      return cachedCategories.value;
    }
    const { data, error } = await execute();
    if (error) {
      throw new Error(error.message);
    }
    cachedCategories.value = sortByPosition(
      toNonNullable(data?.categories?.items?.[0]?.children)?.filter((c) => !!c).map((c) => mapMegaMenuCategoryTree(c))
    );
    return cachedCategories.value;
  })();
  const flatCategories = computed(() => {
    return flatten(categories || []);
  });
  const findBySlug = (slug) => {
    return flatCategories.value.find(
      (c) => "url_key" in c && c.url_key === slug
    );
  };
  return {
    categories: flatten(categories || []).filter(
      (category) => category.includeInMenu === 1
    ),
    findBySlug
  };
}
export async function useHomeFeaturedCategories(opts = {
  flat: false
}, _fetchCategories = fetchCategories) {
  const { cacheParam } = useSetLocaleToCacheParams();
  const cachedCategories = useCachedSsrRef(cacheParam("home-page-featured-categories"), []);
  const { execute } = _fetchCategories();
  if (!cachedCategories.value.length) {
    const { data, error } = await execute();
    if (error) {
      throw new Error(error.message);
    }
    cachedCategories.value = flatten(
      sortByPosition(
        toNonNullable(data?.categories?.items?.[0]?.children)?.filter((c) => !!c).map((c) => mapHomePageCategoryTree(c))
      )
    );
  }
  return cachedCategories.value?.filter((category) => category.includeInHomePage === 1).slice(0, 9);
}
export function mapCategoryTree(apiItem) {
  return {
    ...apiItem,
    meta_title: apiItem.meta_title || "",
    meta_description: apiItem.meta_description || "",
    meta_keywords: apiItem.meta_keywords || "",
    position: apiItem.position || 0,
    order: Number(apiItem.featured_order) || 0,
    level: (apiItem.level ?? 1) - 1,
    children: apiItem.children ? toNonNullable(apiItem.children).map(mapCategoryTree) : void 0
  };
}
export function mapMegaMenuCategoryTree(apiItem) {
  return {
    id: apiItem.id,
    uid: apiItem.uid,
    name: apiItem.name,
    url_key: apiItem.url_key,
    includeInMenu: apiItem.includeInMenu,
    meta_title: apiItem.meta_title,
    meta_description: apiItem.meta_description,
    level: (apiItem.level ?? 1) - 1,
    ...apiItem.offer ? { offer: apiItem.offer } : {},
    image: apiItem.image,
    path: apiItem.path,
    order: Number(apiItem?.featured_order) || 0,
    children: apiItem.children ? toNonNullable(apiItem.children)?.filter((c) => !!c).filter((c) => c.includeInMenu === 1)?.map((c) => mapMegaMenuCategoryTree(c)) : void 0
  };
}
export function mapHomePageCategoryTree(apiItem) {
  return {
    id: apiItem.id,
    uid: apiItem.uid,
    name: apiItem.name,
    url_key: apiItem.url_key,
    includeInHomePage: apiItem.includeInHomePage,
    meta_title: apiItem.meta_title,
    meta_description: apiItem.meta_description,
    level: (apiItem.level ?? 1) - 1,
    ...apiItem.offer ? { offer: apiItem.offer } : {},
    image: apiItem.image,
    path: apiItem.path,
    order: Number(apiItem.featured_order) || 0,
    children: apiItem.children ? toNonNullable(apiItem.children)?.filter((c) => !!c).map((c) => mapHomePageCategoryTree(c)) : void 0
  };
}
function flatten(arr) {
  return arr.reduce((acc, value) => {
    const category = { ...value };
    acc.push(category);
    if (value.children) {
      acc = acc.concat(flatten(value.children));
    }
    return acc;
  }, []);
}
function sortByPosition(arr) {
  const sorted = [];
  arr.forEach((item) => {
    if (item.children && item.children.length) {
      item.children = sortByPosition(item.children);
    }
    sorted.push(item);
  });
  sorted.sort(
    (a, b) => (a.position || Number.MAX_VALUE) - (b.position || Number.MAX_VALUE)
  );
  return sorted;
}
